import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Menu, X } from 'lucide-react';

const TermsPage: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const styles = {
    container: { maxWidth: '1200px', margin: '0 auto', padding: '0 24px' },
    nav: { borderBottom: '1px solid #e8eaed', padding: '16px 0', position: 'sticky' as const, top: 0, zIndex: 1000, backdropFilter: 'blur(10px)', backgroundColor: 'rgba(255, 255, 255, 0.95)' },
    navContainer: { maxWidth: '1200px', margin: '0 auto', padding: '0 16px', display: 'flex', justifyContent: isMobile ? 'flex-start' : 'space-between', alignItems: 'center', gap: isMobile ? '12px' : '0' },
    logo: { fontSize: isMobile ? '20px' : '24px', fontWeight: 700, color: '#14B8A6', textDecoration: 'none', letterSpacing: '-0.5px' },
    navLinks: { display: 'flex', gap: '32px', alignItems: 'center' },
    mobileMenu: { position: 'fixed' as const, top: 0, left: 0, width: '80%', maxWidth: '400px', height: '100vh', background: '#ffffff', zIndex: 2000, padding: '80px 24px 24px', display: 'flex', flexDirection: 'column' as const, gap: '24px' },
    mobileMenuOverlay: { position: 'fixed' as const, top: 0, left: 0, width: '100%', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 1999 },
    hamburger: { background: 'none', border: 'none', cursor: 'pointer', padding: '8px' },
    closeButton: { position: 'absolute' as const, top: '16px', right: '16px', background: 'none', border: 'none', cursor: 'pointer', padding: '8px' },
    navLink: { color: '#5f6368', textDecoration: 'none', fontSize: '14px', fontWeight: 500, transition: 'color 0.2s' },
    primaryButton: { background: '#14B8A6', color: '#ffffff', padding: '12px 24px', borderRadius: '24px', textDecoration: 'none', fontSize: '14px', fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'all 0.2s', display: 'inline-flex', alignItems: 'center', gap: '8px', boxShadow: '0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)' },
    content: { maxWidth: '780px', margin: '0 auto', padding: isMobile ? '40px 16px 60px' : '48px 24px 80px' },
    h2: { fontSize: '22px', fontWeight: 500, color: '#202124', marginTop: '40px', marginBottom: '16px' },
    h3: { fontSize: '17px', fontWeight: 500, color: '#202124', marginTop: '24px', marginBottom: '8px' },
    p: { fontSize: '15px', color: '#5f6368', lineHeight: 1.8, marginBottom: '16px' },
    ul: { paddingLeft: '20px', marginBottom: '16px' },
    li: { fontSize: '15px', color: '#5f6368', lineHeight: 1.8, marginBottom: '6px' },
  };

  return (
    <>
      <Helmet>
        <title>Terms of Service — HUSLRZ</title>
        <meta name="description" content="HUSLRZ Terms of Service. Read the terms and conditions governing your use of the HUSLRZ platform." />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      </Helmet>

      <div style={{ fontFamily: 'Google Sans, Roboto, Arial, sans-serif', color: '#202124' }}>
        {/* Navigation */}
        <nav style={styles.nav}>
          <div style={styles.navContainer}>
            {isMobile && <button style={styles.hamburger} onClick={() => setMobileMenuOpen(true)}><Menu size={24} color="#202124" /></button>}
            <Link to="/" style={styles.logo}>HUSLRZ</Link>
            {!isMobile && (
              <div style={styles.navLinks}>
                <Link to="/partners" style={styles.navLink}>For Partners</Link>
                <Link to="/corporate" style={styles.navLink}>For Corporates</Link>
                <Link to="/about" style={styles.navLink}>About</Link>
                <Link to="/contact" style={styles.navLink}>Contact</Link>
                <Link to="/login" style={{ ...styles.navLink, color: '#14B8A6', fontWeight: 600 }}>Login</Link>
                <Link to="/signup" style={styles.primaryButton}>Get Started <ArrowRight size={16} /></Link>
              </div>
            )}
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMobile && mobileMenuOpen && (
          <>
            <div style={styles.mobileMenuOverlay} onClick={() => setMobileMenuOpen(false)} />
            <div style={styles.mobileMenu}>
              <button style={styles.closeButton} onClick={() => setMobileMenuOpen(false)}><X size={24} color="#202124" /></button>
              <Link to="/partners" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>For Partners</Link>
              <Link to="/corporate" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>For Corporates</Link>
              <Link to="/about" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>About</Link>
              <Link to="/contact" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>Contact</Link>
              <Link to="/login" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0', color: '#14B8A6', fontWeight: 600 }} onClick={() => setMobileMenuOpen(false)}>Login</Link>
              <Link to="/signup" style={{ ...styles.primaryButton, width: '100%', justifyContent: 'center', padding: '16px 24px', fontSize: '16px', marginTop: '16px' }} onClick={() => setMobileMenuOpen(false)}>Get Started <ArrowRight size={18} /></Link>
            </div>
          </>
        )}

        {/* Content */}
        <div style={styles.content}>
          <h1 style={{ fontSize: 'clamp(28px, 6vw, 40px)', fontWeight: 400, color: '#202124', marginBottom: '8px' }}>Terms of Service</h1>
          <p style={{ fontSize: '14px', color: '#9CA3AF', marginBottom: '32px' }}>Last updated: March 2, 2026</p>

          <p style={styles.p}>
            Welcome to HUSLRZ. These Terms of Service ("Terms") govern your access to and use of the HUSLRZ mobile application, website, and related services (collectively, the "Platform"). By creating an account or using the Platform, you agree to these Terms. If you do not agree, please do not use the Platform.
          </p>

          <h2 style={styles.h2}>1. About HUSLRZ</h2>
          <p style={styles.p}>
            HUSLRZ is a technology marketplace platform that connects fitness enthusiasts ("Users") with fitness facilities ("Partners") including gyms, yoga studios, swimming pools, turfs, courts, and other activity venues. HUSLRZ does not own, operate, or manage any fitness facility. HUSLRZ acts as an intermediary that facilitates bookings and payments between Users and Partners.
          </p>

          <h2 style={styles.h2}>2. Eligibility</h2>
          <ul style={styles.ul}>
            <li style={styles.li}>You must be at least 18 years of age to use HUSLRZ</li>
            <li style={styles.li}>You must provide accurate, current, and complete information during registration</li>
            <li style={styles.li}>You are responsible for maintaining the security of your account credentials</li>
          </ul>

          <h2 style={styles.h2}>3. How HUSLRZ Works</h2>
          <h3 style={styles.h3}>For Users:</h3>
          <ul style={styles.ul}>
            <li style={styles.li}>Browse partner facilities and available time slots</li>
            <li style={styles.li}>Book sessions by paying from your HUSLRZ Wallet</li>
            <li style={styles.li}>Check in at the facility using your unique QR code</li>
            <li style={styles.li}>Rate your experience after the session</li>
          </ul>
          <h3 style={styles.h3}>HUSLRZ Wallet:</h3>
          <ul style={styles.ul}>
            <li style={styles.li}>Load funds into your HUSLRZ Wallet to book sessions</li>
            <li style={styles.li}>Wallet balances may include loaded funds, bonus credits, referral credits, and corporate credits</li>
            <li style={styles.li}>Bonus, referral, and corporate credits may have expiry dates and usage conditions</li>
            <li style={styles.li}>Loaded funds (your own money) do not expire and are refundable</li>
          </ul>

          <h2 style={styles.h2}>4. Bookings & Cancellations</h2>
          <ul style={styles.ul}>
            <li style={styles.li}>A booking is confirmed once payment is deducted from your Wallet</li>
            <li style={styles.li}>Cancellations made 24+ hours before the session receive a full refund to your Wallet</li>
            <li style={styles.li}>Cancellations made 6-24 hours before receive a 75% refund</li>
            <li style={styles.li}>Cancellations made 2-6 hours before receive a 50% refund</li>
            <li style={styles.li}>Cancellations made less than 2 hours before receive no refund</li>
            <li style={styles.li}>No-shows (failure to check in) receive no refund</li>
            <li style={styles.li}>If a Partner cancels your booking, you receive a full refund plus an inconvenience credit</li>
          </ul>

          <h2 style={styles.h2}>5. User Conduct</h2>
          <p style={styles.p}>When using HUSLRZ and visiting partner facilities, you agree to:</p>
          <ul style={styles.ul}>
            <li style={styles.li}>Follow the rules and guidelines of each partner facility</li>
            <li style={styles.li}>Arrive on time for your booked sessions</li>
            <li style={styles.li}>Treat facility staff and other users with respect</li>
            <li style={styles.li}>Not engage in any illegal, fraudulent, or harmful activity</li>
            <li style={styles.li}>Not misuse referral programmes, wallet credits, or promotional offers</li>
            <li style={styles.li}>Provide honest and fair ratings and reviews</li>
          </ul>

          <h2 style={styles.h2}>6. Partner Facilities</h2>
          <p style={styles.p}>
            HUSLRZ verifies partner facilities before listing, but does not control or guarantee the quality, safety, or suitability of any facility. Each partner is an independent business. HUSLRZ is not responsible for injuries, accidents, or losses occurring at partner facilities. By booking a session, you acknowledge that physical activity carries inherent risks.
          </p>

          <h2 style={styles.h2}>7. Payments & Pricing</h2>
          <ul style={styles.ul}>
            <li style={styles.li}>All prices displayed on the Platform are in Indian Rupees (INR) and include applicable taxes</li>
            <li style={styles.li}>Payments are processed securely through authorised payment gateways</li>
            <li style={styles.li}>HUSLRZ does not store your credit/debit card information</li>
            <li style={styles.li}>Prices may vary by facility, time of day, and demand (dynamic pricing)</li>
          </ul>

          <h2 style={styles.h2}>8. Intellectual Property</h2>
          <p style={styles.p}>
            All content on the Platform — including the HUSLRZ name, logo, design, text, graphics, and software — is the property of HUSLRZ or its licensors and is protected by intellectual property laws. You may not copy, modify, distribute, or create derivative works without written permission.
          </p>

          <h2 style={styles.h2}>9. Limitation of Liability</h2>
          <p style={styles.p}>
            To the maximum extent permitted by law, HUSLRZ's total liability for any claims arising from your use of the Platform shall not exceed the total amount you have paid to HUSLRZ in the 12 months preceding the claim. HUSLRZ shall not be liable for any indirect, incidental, special, or consequential damages.
          </p>

          <h2 style={styles.h2}>10. Dispute Resolution</h2>
          <p style={styles.p}>
            Any disputes arising from these Terms shall first be attempted to be resolved through HUSLRZ's internal grievance mechanism. If unresolved within 30 days, disputes shall be referred to arbitration under the Arbitration and Conciliation Act, 1996, with the seat of arbitration in Chennai, Tamil Nadu. These Terms are governed by the laws of India, and the courts of Chennai shall have exclusive jurisdiction.
          </p>

          <h2 style={styles.h2}>11. Account Termination</h2>
          <p style={styles.p}>
            You may delete your account at any time through the app settings. Loaded wallet funds will be refunded to your original payment method. HUSLRZ may suspend or terminate accounts that violate these Terms, engage in fraudulent activity, or cause harm to the platform or community.
          </p>

          <h2 style={styles.h2}>12. Changes to These Terms</h2>
          <p style={styles.p}>
            We may update these Terms from time to time. Material changes will be communicated via email or in-app notification at least 15 days before taking effect. Continued use of the Platform after changes constitutes acceptance.
          </p>

          <h2 style={styles.h2}>13. Contact</h2>
          <p style={styles.p}>
            If you have questions about these Terms, contact us at:
          </p>
          <p style={styles.p}>
            <strong>HUSLRZ</strong><br />
            Email: <a href="mailto:support@huslrz.com" style={{ color: '#14B8A6', textDecoration: 'none' }}>support@huslrz.com</a><br />
            Grievance Officer: <a href="mailto:support@huslrz.com" style={{ color: '#14B8A6', textDecoration: 'none' }}>support@huslrz.com</a><br />
            Chennai, Tamil Nadu, India
          </p>
        </div>

        {/* Footer */}
        <footer style={{ background: '#111827', padding: isMobile ? '40px 16px' : '48px 24px', color: '#ffffff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#14B8A6' }}>HUSLRZ</div>
              <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                <Link to="/privacy" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Privacy Policy</Link>
                <Link to="/terms" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Terms of Service</Link>
                <Link to="/contact" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Contact</Link>
              </div>
            </div>
            <div style={{ borderTop: '1px solid #374151', marginTop: '24px', paddingTop: '24px', textAlign: 'center' as const }}>
              <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>&copy; {new Date().getFullYear()} HUSLRZ. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default TermsPage;
